<template>
  <canvas ref="canvasRef"></canvas>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';

export default {
  name: 'FireWorkComponent',
  setup() {
    const canvasRef = ref(null);
    let animationFrameId;

    onMounted(() => {
      const canvas = canvasRef.value;
      const ctx = canvas.getContext('2d');
      let width = (canvas.width = window.innerWidth);
      let height = (canvas.height = window.innerHeight);

      window.addEventListener('resize', () => {
        width = canvas.width = window.innerWidth;
        height = canvas.height = window.innerHeight;
      });

      const fireworks = [];
      const particles = [];

      function random(min, max) {
        return Math.random() * (max - min) + min;
      }

      class Firework {
        constructor() {
          this.x = random(width * 0.4, width * 0.6);
          this.y = height;
          
          // Set a strong initial upward velocity and a slight horizontal velocity
          this.vx = 0;
          this.vy = -random(4, 6);

          this.size = 2;
          this.hue = random(0, 360);
          this.brightness = random(50, 60);
          this.alpha = 1;
          this.trail = [];
          this.gravity = 0.025; // Gravity effect
        }

        update() {
          // Add current position to the trail
          this.trail.push({ x: this.x, y: this.y, alpha: this.alpha });
          // Keep the trail from getting too long
          if (this.trail.length > 8) {
            this.trail.shift();
          }
          // Fade the trail itself
          this.alpha *= 0.97;

          // Apply gravity to vertical velocity
          this.vy += this.gravity;

          // Update position
          this.x += this.vx;
          this.y += this.vy;

          // Explode at the apex of the trajectory (when vertical velocity is zero or positive)
          if (this.vy >= 0) {
            this.explode();
            return true; 
          }
          return false;
        }

        draw() {
          ctx.globalCompositeOperation = 'lighter';
          ctx.strokeStyle = `hsl(${this.hue}, 100%, ${this.brightness}%)`;
          ctx.lineWidth = this.size;
          ctx.beginPath();
          ctx.moveTo(this.trail[0]?.x, this.trail[0]?.y);
          ctx.lineTo(this.x, this.y);
          ctx.stroke();
        }

        explode() {
          const particleCount = 150;
          for (let i = 0; i < particleCount; i++) {
            particles.push(new Particle(this.x, this.y, this.hue));
          }
        }
      }

      class Particle {
        constructor(x, y, hue) {
          this.x = x;
          this.y = y;
          this.speed = random(1, 10);
          this.angle = random(0, Math.PI * 2);
          this.vx = Math.cos(this.angle) * this.speed;
          this.vy = Math.sin(this.angle) * this.speed;
          this.hue = hue;
          this.brightness = random(50, 80);
          this.alpha = 1;
          this.decay = random(0.0043, 0.0086);
          this.gravity = 0.05;
          this.trail = [];
        }

        update() {
          this.trail.push({ x: this.x, y: this.y, alpha: this.alpha });
          if (this.trail.length > 5) {
            this.trail.shift();
          }

          this.vx *= 0.98;
          this.vy *= 0.98;
          this.vy += this.gravity;
          this.x += this.vx;
          this.y += this.vy;
          this.alpha -= this.decay;

          return this.alpha <= this.decay;
        }

        draw() {
          ctx.globalCompositeOperation = 'lighter';
          ctx.strokeStyle = `hsla(${this.hue}, 100%, ${this.brightness}%, ${this.alpha})`;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(this.trail[0]?.x, this.trail[0]?.y);
          ctx.lineTo(this.x, this.y);
          ctx.stroke();
        }
      }

      function createFirework() {
        fireworks.push(new Firework());
      }

      let lastLaunch = 0;
      function loop(timestamp) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'lighter';

        if (timestamp - lastLaunch > 972) {
          createFirework();
          lastLaunch = timestamp;
        }
        
        for (let i = fireworks.length - 1; i >= 0; i--) {
          if (fireworks[i].update()) {
            fireworks.splice(i, 1);
          } else {
            fireworks[i].draw();
          }
        }

        for (let i = particles.length - 1; i >= 0; i--) {
          if (particles[i].update()) {
            particles.splice(i, 1);
          } else {
            particles[i].draw();
          }
        }

        animationFrameId = requestAnimationFrame(loop);
      }

      loop(0);
    });

    onUnmounted(() => {
      cancelAnimationFrame(animationFrameId);
    });

    return {
      canvasRef,
    };
  },
};
</script>

<style scoped>
canvas {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}
</style>